import { z } from "zod";

/**
 * Phone number types available for regulatory bundles.
 * Maps to Twilio's number type classification.
 */
export const PHONE_NUMBER_TYPES = ["local", "mobile", "toll_free", "national"] as const;

/**
 * End-user types for regulation lookup.
 * Determines which regulatory requirements apply.
 */
export const END_USER_TYPES = ["individual", "business"] as const;

/**
 * Form data for regulation-driven compliance submission.
 *
 * Step 1 fields are fixed (country, phoneNumberType, endUserType).
 * Steps 2-3 are dynamic — fields come from the regulation requirements API.
 */
export const ComplianceFormSchema = z.object({
  // Step 1: Country, Type & End-User Type (fixed)
  country: z.string().length(2),
  phoneNumberType: z.enum(PHONE_NUMBER_TYPES),
  endUserType: z.enum(END_USER_TYPES),

  // Step 2: End-User fields (dynamic — keys come from regulation API)
  regulationSid: z.string().optional(),
  endUserFields: z.record(z.string(), z.string()).default({}),

  // Step 3: Document selection and fields (dynamic)
  /** Maps `requirementName` (Twilio's stable AND-requirement identifier, e.g.
   *  `business_name_info`) to the selected accepted-document index. Keying
   *  by name (not array index) keeps state stable across refetches that
   *  re-order the requirements array. */
  selectedDocumentTypes: z.record(z.string(), z.number()).default({}),
  /** Maps document type string to its field values (machineName -> value).
   *  Keyed by document type — when multiple AND requirements share a type
   *  (e.g. BE-business uses `commercial_registrar_excerpt` for all three),
   *  the field state is shared, which mirrors the backend dedupe semantics. */
  documentFields: z.record(z.string(), z.record(z.string(), z.string())).default({}),
  /** Twilio Address SID selected in the address picker (when regulation requiresAddress) */
  selectedAddressSid: z.string().optional()
});
export type ComplianceFormData = z.infer<typeof ComplianceFormSchema>;

/**
 * Per-step validation schemas for progressive form validation.
 *
 * Steps 2 and 3 use dynamic validation against regulation requirements
 * (not Zod schemas) since the fields vary per country/type/endUserType.
 * Only step 1 (countryAndType) has a static Zod schema.
 */
export const ComplianceStepSchemas = {
  countryAndType: z.object({
    country: ComplianceFormSchema.shape.country,
    phoneNumberType: ComplianceFormSchema.shape.phoneNumberType,
    endUserType: ComplianceFormSchema.shape.endUserType
  })
} as const;
